import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { useCRM } from '../context/CRMContext';
import BaseModal from '../components/modals/BaseModal';
import CustomerRoomModal from '../components/modals/CustomerRoomModal';

const CustomerRooms = () => {
  const { customerRooms, companies } = useCRM();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Combine room data with company data
  const enrichedRooms = customerRooms.map(room => {
    const company = companies.find(c => c.company_id === room.company_id);
    return { ...room, companyName: company ? company.name : 'Ukjent selskap', companyLogo: company?.logo, companyId: company?.company_id, industry: company?.industry };
  }).filter(room => room.companyName.toLowerCase().includes(searchTerm.toLowerCase()));

  const handleEditRoom = (companyId) => {
    const company = companies.find(c => c.company_id === companyId);
    if (company) {
      setSelectedCompany(company);
      setIsModalOpen(true);
    }
  };

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-10">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-slate-200">
        <div className="space-y-2">
          <div className="flex items-center gap-2 mb-2">
            <div className="px-2 py-1 bg-primary-100 text-primary-700 text-[10px] font-black uppercase tracking-widest rounded">Beta</div>
          </div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tight">Kunderom</h1>
          <p className="text-slate-500 font-medium text-lg max-w-2xl">
            Del dokumenter, planer og avtaler i et sikkert, merkevarebygget miljø for dine kunder.
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative group">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <SafeIcon icon="🔍" className="h-5 w-5 text-slate-400 group-focus-within:text-primary-500 transition-colors" />
            </div>
            <input type="text" placeholder="Søk i rom..." className="pl-12 pr-6 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 outline-none w-full md:w-80 shadow-sm transition-all" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
          <button onClick={() => navigate('/companies')} className="hidden md:flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-2xl font-bold hover:bg-slate-800 transition-all shadow-lg shadow-slate-200">
            <SafeIcon icon="➕" className="w-5 h-5" /> Nytt rom
          </button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Aktive rom', value: enrichedRooms.length, icon: "📂", color: 'text-blue-600' },
          { label: 'Delte dokumenter', value: enrichedRooms.reduce((acc, r) => acc + (r.documents?.length || 0), 0), icon: "📄", color: 'text-indigo-600' },
          { label: 'Visninger (24t)', value: 12, icon: "👁️", color: 'text-emerald-600' },
          { label: 'Sikkerhetsstatus', value: '100%', icon: "🛡️", color: 'text-emerald-600' }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-2 rounded-xl bg-slate-50 ${stat.color}`}>
                <SafeIcon icon={stat.icon} className="w-5 h-5" />
              </div>
            </div>
            <p className="text-2xl font-black text-slate-900">{stat.value}</p>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Grid of Rooms */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {enrichedRooms.length > 0 ? (
          enrichedRooms.map((room, idx) => (
            <motion.div key={room.room_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.1 }} className="group bg-white rounded-[2rem] border border-slate-200 hover:border-primary-300 shadow-sm hover:shadow-2xl hover:shadow-primary-500/10 transition-all duration-500 flex flex-col h-full overflow-hidden">
              <div className="p-8 flex-1">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center p-3 border border-slate-100 group-hover:scale-110 transition-transform duration-500">
                      {room.companyLogo ? (
                        <img src={room.companyLogo} alt={room.companyName} className="w-full h-full object-contain" />
                      ) : (
                        <SafeIcon icon="📂" className="w-8 h-8 text-primary-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-black text-slate-900 tracking-tight group-hover:text-primary-600 transition-colors line-clamp-1">{room.companyName}</h3>
                      <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">{room.industry || 'Bedrift'}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="p-4 rounded-2xl bg-slate-50 border border-slate-100 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Dokumenter</span>
                      <span className="font-black text-slate-900">{room.documents?.length || 0}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 font-bold uppercase tracking-wider text-[10px]">Siste aktivitet</span>
                      <span className="font-black text-slate-900">{room.created_at}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-8 py-6 bg-slate-50/50 border-t border-slate-100 flex items-center justify-between gap-4">
                <button onClick={() => handleEditRoom(room.company_id)} className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-white border border-slate-200 text-slate-700 font-bold text-sm rounded-xl hover:bg-white hover:border-primary-500 hover:text-primary-600 transition-all shadow-sm">
                  <SafeIcon icon="✏️" className="w-4 h-4" /> Admin
                </button>
                <a href={`/#/portal/${room.room_id}`} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-primary-600 text-white font-bold text-sm rounded-xl hover:bg-primary-700 transition-all shadow-lg shadow-primary-200">
                  Portalen <SafeIcon icon="➡️" className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full py-24 text-center bg-white rounded-[3rem] border-2 border-dashed border-slate-200">
            <div className="w-24 h-24 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <SafeIcon icon="📂" className="w-10 h-10 text-slate-300" />
            </div>
            <h3 className="text-2xl font-black text-slate-900 mb-2">Ingen aktive kunderom</h3>
            <p className="text-slate-500 font-medium max-w-xs mx-auto mb-8">
              Gå til et selskaps profil for å opprette deres første sikre portal.
            </p>
            <button onClick={() => navigate('/companies')} className="px-8 py-3 bg-slate-900 text-white rounded-2xl font-bold shadow-xl shadow-slate-200 hover:scale-105 transition-transform">
              Finn selskap
            </button>
          </div>
        )}
      </div>

      <BaseModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={selectedCompany ? `Kunderom: ${selectedCompany.name}` : 'Kunderom'}>
        {selectedCompany && (
          <CustomerRoomModal company={selectedCompany} onClose={() => setIsModalOpen(false)} />
        )}
      </BaseModal>
    </div>
  );
};

export default CustomerRooms;